/** Frontend API wrapper for workspace-scoped invoices.
 *
 * Mirrors CashFlowPlansService: one static method per route, getAuthHeaders()
 * on every call, all errors surfaced via thrown Error.
 */

import { FINANCE_CHANGED_EVENT } from './SalesSummaryService';
import { API_BASE, getAuthHeaders } from '../utils/supabase/client';
import type { Invoice, InvoiceType, InvoiceStatus } from '../types/accounting';

async function readError(res: Response): Promise<string> {
  try {
    const d = await res.json();
    return (d && (d.error || d.message)) || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

/** The server's invoice page, so a register this long or shorter is whole in one page. */
export const INVOICE_PAGE_SIZE = 25;

function sortedInvoices(rows:Invoice[]):Invoice[] {
 const timestamp=(value:unknown)=>{const parsed=typeof value==='string'?Date.parse(value):NaN;return Number.isFinite(parsed)?parsed:-Infinity;};
 return [...rows].sort((a,b)=>{const left=timestamp(a.issueDate),right=timestamp(b.issueDate);return left===right?String(b.id??'').localeCompare(String(a.id??'')):left>right?-1:1;});
}

export class InvoicesService {
  static async list(params: {
    workspaceId: string;
    type?: InvoiceType;
    status?: InvoiceStatus;
    interimApplicationId?: string;
  }): Promise<Invoice[]> {
    const p = new URLSearchParams({ workspaceId: params.workspaceId });
    if (params.type) p.set('type', params.type);
    if (params.status) p.set('status', params.status);
    if (params.interimApplicationId) p.set('interimApplicationId', params.interimApplicationId);
    p.set('paged','true');p.set('limit',String(INVOICE_PAGE_SIZE));
    const rows:Invoice[]=[];const ids=new Set<string>();const cursors=new Set<string>();
    for(let page=0;page<1000;page++){
      const res=await fetch(`${API_BASE}/invoices?${p}`,{headers:await getAuthHeaders()});
      if(!res.ok)throw new Error(await readError(res));
      const body=await res.json();
      // Older backends ignore the optional paging query and keep their array.
      if(Array.isArray(body)){if(page!==0)throw new Error('Invoice pagination changed. Retry.');return sortedInvoices(body);}
      if(!body||!Array.isArray(body.invoices)||(body.nextCursor!==null&&(typeof body.nextCursor!=='string'||!body.nextCursor||body.nextCursor.length>256)))throw new Error('The invoice page could not be confirmed.');
      for(const invoice of body.invoices){if(!invoice||typeof invoice.id!=='string'||invoice.workspaceId!==params.workspaceId||(params.type!==undefined&&invoice.invoiceType!==params.type))throw new Error('The invoice page scope could not be confirmed.');if(!ids.has(invoice.id)){ids.add(invoice.id);rows.push(invoice);}}
      if(body.nextCursor===null)return sortedInvoices(rows);
      if(!body.invoices.length||cursors.has(body.nextCursor))throw new Error('Invoice pagination did not advance. Retry.');
      cursors.add(body.nextCursor);p.set('cursor',body.nextCursor);
    }
    throw new Error('The invoice list is too large to load safely.');
  }

  /** One server page, never the whole register: the rows, and whether they are all of it. */
  static async firstPage(params: { workspaceId: string; type: InvoiceType }): Promise<{ invoices: Invoice[]; complete: boolean }> {
    const p = new URLSearchParams({ workspaceId: params.workspaceId, type: params.type, paged: 'true', limit: String(INVOICE_PAGE_SIZE) });
    const res = await fetch(`${API_BASE}/invoices?${p}`, { headers: await getAuthHeaders() });
    if (!res.ok) throw new Error(await readError(res));
    const body = await res.json();
    const rows: unknown = Array.isArray(body) ? body : body?.invoices;
    if (!Array.isArray(rows) || (!Array.isArray(body) && body.nextCursor !== null && typeof body.nextCursor !== 'string')) throw new Error('The invoice page could not be confirmed.');
    if (rows.some(invoice => !invoice || typeof invoice.id !== 'string' || invoice.workspaceId !== params.workspaceId || invoice.invoiceType !== params.type)) throw new Error('The invoice page scope could not be confirmed.');
    return { invoices: sortedInvoices(rows as Invoice[]), complete: Array.isArray(body) || body.nextCursor === null };
  }

  static async getById(id: string): Promise<Invoice> {
    const res = await fetch(`${API_BASE}/invoices/${encodeURIComponent(id)}`, {
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async create(data: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>): Promise<Invoice> {
    const res = await fetch(`${API_BASE}/invoices`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw Object.assign(new Error(await readError(res)), { status: res.status });
    const invoice: Invoice = await res.json();
    window.dispatchEvent(new Event(FINANCE_CHANGED_EVENT));
    return invoice;
  }

  static async update(id: string, data: Partial<Invoice> & { expectedUpdatedAt?: string }): Promise<Invoice> {
    const res = await fetch(`${API_BASE}/invoices/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readError(res));
    const invoice: Invoice = await res.json();
    window.dispatchEvent(new Event(FINANCE_CHANGED_EVENT));
    return invoice;
  }

  /** Deletes a draft; any other status is voided by the server. `expectedUpdatedAt` refuses the write if someone else changed the invoice since it was read. */
  static async remove(id: string, expectedUpdatedAt?: string): Promise<void> {
    const query = expectedUpdatedAt ? `?expectedUpdatedAt=${encodeURIComponent(expectedUpdatedAt)}` : '';
    const res = await fetch(`${API_BASE}/invoices/${encodeURIComponent(id)}${query}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw Object.assign(new Error(await readError(res)), { status: res.status });
    window.dispatchEvent(new Event(FINANCE_CHANGED_EVENT));
  }

  static async syncToXero(id: string): Promise<{ status: string; message: string }> {
    const res = await fetch(`${API_BASE}/invoices/${encodeURIComponent(id)}/xero-sync`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    const result = await res.json();
    window.dispatchEvent(new Event(FINANCE_CHANGED_EVENT));
    return result;
  }
}
